import React, { useEffect, useState } from 'react';
import ErrorPage from '../../../../../../common/src/components/ErrorPage';

// Components
// Vin
import VinCheck from './VinCheck';
import VinSearch from './VinSearch';
// License plate
import LicensePlateCheck from './LicensePlateCheck';
import LicensePlateSearch from './LicensePlateSearch';
// Others
import CarSearch from './CarSearch';
import VehicleData from './VehicleData';

// Redux

// Types
import { Car } from '../../../../../../../types/SMT';
import { FlowsQuestion } from '../../../../types/FlowsTypes';
import useSubView from '../../../../hooks/useSubView';
import useFlowInputComponent from '../../../../hooks/useFlowsInputComponent';
import { useFlowsDispatch } from '../../../../../../common/src/utils/globalVariables';
import { prefillAnswer } from '../../../../store/redux/slices/flowsSlice';
import { useFlowsProgression } from '../../../../hooks/useFlowsProgression';
import useIsNLplan from '../../../../../../common/src/hooks/useIsNLPlan';

interface Props {
  question: FlowsQuestion;
  withVin?: boolean;
  withLicensePlate?: boolean;
}

const CarInputComponent: React.FC<React.PropsWithChildren<Props>> = ({
  question,
  withVin = true,
  withLicensePlate,
}) => {
  const flowsDispatch = useFlowsDispatch();
  const [initialVin, setInitialVin] = useState<string>();
  const [initialLicensePlate, setInitialLicensePlate] = useState<string>();

  const { isNL } = useIsNLplan();
  const defaultView = withLicensePlate ? 'licensePlateCheck' : 'vinCheck';
  const {
    currentSubView,
    nextSubView,
    previousSubView,
    hasAnswer,
    changeSubView,
  } = useSubView({
    question,
    defaultView,
    defaultViewForDefinedAnswer: 'vehicleData',
  });

  const { nextQuestion, previousQuestion } = useFlowsProgression();

  const {
    field: { value, onBlur },
  } = useFlowInputComponent({
    question,
  });

  useEffect(() => {
    // Vin
    const vin = (value as Car)?.chassis_number;
    if (vin && !initialVin) setInitialVin(vin);

    // License plate
    const licensePlate = (value as Car)?.number_plate;
    if (licensePlate && !initialLicensePlate) {
      setInitialLicensePlate(licensePlate);
    }
  }, []);

  const handleOnChange = (vehicle: Car | undefined) => {
    let adjustedCarValue = vehicle?.value;
    if (vehicle?.vat_rate && vehicle.value && isNL)
      adjustedCarValue = vehicle.value / (1 + vehicle.vat_rate);

    onBlur({
      name: question.id,
      value: {
        ...vehicle,
        ...(adjustedCarValue != null && { value: adjustedCarValue }),
      },
    });

    if (vehicle?.chassis_number) setInitialVin(vehicle.chassis_number);
    if (vehicle?.number_plate) setInitialLicensePlate(vehicle.number_plate);
    // Set default value for this question

    flowsDispatch(
      prefillAnswer({
        id: 'car.catalogue_value',
        answer: adjustedCarValue || vehicle?.price || vehicle?.estimated_value,
      }),
    );

    flowsDispatch(
      prefillAnswer({
        id: 'car.registration_first',
        answer: vehicle?.registration_first,
      }),
    );

    flowsDispatch(
      prefillAnswer({
        id: 'car.model_year',
        answer: vehicle?.model_year,
      }),
    );
  };

  const handleNewSearch = () => {
    changeSubView(defaultView);
    handleOnChange({});
  };

  return (
    <>
      {{
        // VIN
        vinCheck: (
          <VinCheck
            nextSubView={nextSubView}
            previousQuestionHandler={previousQuestion}
          />
        ),
        vinSearch: (
          <VinSearch
            handleChange={handleOnChange}
            nextSubView={nextSubView}
            previousSubView={previousSubView}
            initialVin={initialVin}
          >
            Vin Search
          </VinSearch>
        ),
        // LICENSE PLATE
        licensePlateCheck: (
          <LicensePlateCheck
            nextSubView={nextSubView}
            previousQuestionHandler={previousQuestion}
          />
        ),
        licensePlateSearch: (
          <LicensePlateSearch
            handleChange={handleOnChange}
            nextSubView={nextSubView}
            previousSubView={previousSubView}
            initialLicensePlate={initialLicensePlate}
          >
            License Plate Search
          </LicensePlateSearch>
        ),
        vehicleSearch: (
          <CarSearch
            vehicle={value as Car}
            handleChange={handleOnChange}
            nextSubView={nextSubView}
            previousSubView={
              withVin || withLicensePlate ? previousSubView : previousQuestion
            }
          />
        ),
        vehicleData: (
          <VehicleData
            vehicle={value as Car}
            handleChange={handleOnChange}
            previousSubView={hasAnswer ? previousQuestion : previousSubView}
            nextQuestionHandler={nextQuestion}
            handleNewSearch={handleNewSearch}
          >
            Vehicle data
          </VehicleData>
        ),
      }[currentSubView] || <ErrorPage />}
    </>
  );
};

export default CarInputComponent;
